import React, { CSSProperties } from 'react';
import type { StoryFrame } from 'fym-shared/src/music24/story/storyFrames';
import { storyFormatOf } from 'fym-shared/src/music24/story/storyFormats';
import type { StoryItem } from 'fym-shared/src/music24/story/storyLayout';
import {
  storyDisplayShadow,
  storyHeroStyle,
  storyKickerStyle,
  storyLyricStyle,
  storyNowWordStyle,
  storyPillStyle,
  storyTitleLineStyle,
  storyWhenStyle,
  type StoryColours,
} from 'fym-shared/src/music24/story/storyStyles';
import { keyedStoryLines, lyricLineParts } from 'fym-shared/src/music24/story/storyText';
import type { KitStory } from 'fym-shared/src/music24/story/storyTypes';
import { TypedMemo } from '../../../../common/types';

/** The CSS of one font role: the family and the type style of the theme (`themeType`). */
export type StoryFontCss = Pick<
  CSSProperties,
  'fontFamily' | 'fontWeight' | 'fontStyle' | 'textTransform' | 'letterSpacing'
> & { tracking: number };

type TextItem = Extract<StoryItem, { lines: string[] }>;

function boxCss(item: TextItem): CSSProperties {
  return {
    position: 'absolute',
    left: item.box.x,
    top: item.box.y,
    width: item.box.w,
    height: item.box.h,
    fontSize: item.sizePx,
    lineHeight: item.lineHeight,
    textAlign: item.align,
    whiteSpace: 'nowrap',
  };
}

function css(font: StoryFontCss): CSSProperties {
  const { tracking: _tracking, ...rest } = font;
  return rest;
}

const Lines: React.FC<{ lines: string[]; render?: (line: string) => React.ReactNode }> = ({ lines, render }) => (
  <>
    {keyedStoryLines(lines).map(({ key, line }) => (
      <div key={key}>{render === undefined ? line : render(line)}</div>
    ))}
  </>
);

interface StoryPreviewTextProps {
  item: TextItem;
  story: KitStory;
  frame: StoryFrame;
  themeId: string;
  display: StoryFontCss;
  text: StoryFontCss;
  colors: StoryColours;
  /** The detail factor of the format (`StoryFormat.unit`). */
  unit: number;
}

/**
 * One text part of the Story preview. It is the browser twin of the renderer's `KitStoryText`
 * (music24-api): the same boxes (`storyLayout`) and the same look (fym-shared `storyStyles`).
 */
const StoryPreviewTextComponent: React.FC<StoryPreviewTextProps> = ({
  item,
  story,
  frame,
  themeId,
  display,
  text,
  colors,
  unit,
}) => {
  const base = boxCss(item);
  const displayCss: CSSProperties = { ...css(display), textShadow: storyDisplayShadow(themeId, colors, unit) };
  const chip: CSSProperties = { display: 'flex', justifyContent: 'center', alignItems: 'center', lineHeight: 'normal' };
  switch (item.kind) {
    case 'hero': {
      if (story.moment === 'lyric') {
        const now = storyNowWordStyle(themeId, colors, unit);
        return (
          <div
            data-testid="story-lyric"
            style={{
              ...base,
              ...displayCss,
              ...storyLyricStyle(themeId, frame, colors, unit, storyFormatOf(story.format).lyric?.keepCase === true),
            }}
          >
            <Lines
              lines={item.lines}
              render={(line) =>
                lyricLineParts(line, story.nowWord).map((part) =>
                  part.lit ? (
                    <span key={part.key} style={now}>
                      {part.text}
                    </span>
                  ) : (
                    <React.Fragment key={part.key}>{part.text}</React.Fragment>
                  )
                )
              }
            />
          </div>
        );
      }
      return (
        <div
          data-testid="story-hero"
          style={{
            ...base,
            ...displayCss,
            ...storyHeroStyle(themeId, frame, colors, unit),
            // The render draws the hero digits as lining numbers (`KitStoryText`).
            fontVariantNumeric: 'lining-nums',
          }}
        >
          <Lines lines={item.lines} />
        </div>
      );
    }
    case 'title':
      return (
        <div data-testid="story-title" style={{ ...base, ...displayCss, color: colors.text }}>
          <Lines
            lines={item.lines}
            render={(line) => <span style={storyTitleLineStyle(themeId, colors)}>{line}</span>}
          />
        </div>
      );
    case 'dayLabel':
      return (
        <div style={{ ...base, ...displayCss, color: colors.text }}>
          <Lines lines={item.lines} />
        </div>
      );
    case 'artist':
      return <div style={{ ...base, ...css(text), color: colors.text }}>{item.lines[0]}</div>;
    case 'kicker':
      return (
        <div style={{ ...base, ...chip }}>
          <span style={{ ...css(text), lineHeight: item.lineHeight, ...storyKickerStyle(frame, colors, unit) }}>
            {item.lines[0]}
          </span>
        </div>
      );
    case 'when':
      return (
        <div data-testid="story-when" style={{ ...base, ...chip, alignItems: 'flex-start' }}>
          <span style={{ ...css(text), lineHeight: item.lineHeight, ...storyWhenStyle(themeId, colors, unit) }}>
            {item.lines[0]}
          </span>
        </div>
      );
    case 'pill':
      return (
        <div
          data-testid="story-pill"
          style={{
            ...base,
            ...css(text),
            boxSizing: 'border-box',
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            letterSpacing: `${Math.min(text.tracking, 0.12)}em`,
            ...storyPillStyle(themeId, frame, colors),
          }}
        >
          {item.lines[0]}
        </div>
      );
    case 'link':
      return (
        <div
          data-testid="story-link"
          style={{
            ...base,
            ...css(text),
            fontWeight: 500,
            textTransform: 'none',
            letterSpacing: '0.02em',
            color: colors.text,
          }}
        >
          {item.lines[0]}
        </div>
      );
    default: {
      const exhaustive: never = item;
      return exhaustive;
    }
  }
};

export const StoryPreviewText = TypedMemo(StoryPreviewTextComponent);
